'use client';

import { useState, type CSSProperties } from 'react';
import {
  IconArrowsSort,
  IconBolt,
  IconBrain,
  IconCheck,
  IconChevronDown,
  IconCpu,
  IconCurrencyEuro,
  IconDeviceGamepad2,
  IconPalette,
  IconStack2,
  IconWorld,
  IconX,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { formatCurrency, formatHourlyRate } from '@/lib/utils/currency';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  GPU_PRODUCTS,
  REGION_OPTIONS,
  REGION_GROUPS,
  PRICE_RANGE,
  getAvailableGPUs,
} from '@/lib/data/gpu-products';
import type { AirGPURegion } from '@/lib/integrations/airgpu/types';
import { ARCADE_CLASSES, SORTS, type ArcadeClassId, type SortId } from './arcade-classes';

export interface GPUFilters {
  gpuType: string | null;
  region: AirGPURegion | null;
  priceRange: [number, number];
  showComingSoon: boolean;
}

interface GPUFilterBarProps {
  filters: GPUFilters;
  onFiltersChange: (filters: GPUFilters) => void;
  /** Rentable GPUs in the current view. */
  resultCount: number;
  activeClass: ArcadeClassId;
  onClassChange: (id: ArcadeClassId) => void;
  /** GPUs per class under the other filters. */
  classCounts: Record<ArcadeClassId, number>;
  sort: SortId;
  onSortChange: (sort: SortId) => void;
  /** Resets the URL filters, the class and the sort. */
  onClearAll: () => void;
}

const CLASS_ICONS: Record<ArcadeClassId, typeof IconBolt> = {
  all: IconBolt,
  gaming: IconDeviceGamepad2,
  creative: IconPalette,
  ai: IconBrain,
  heavy: IconStack2,
};

/** Trigger styling: neutral when unset, a lime tint when the filter is applied. */
const triggerClass = (active: boolean) =>
  cn(
    'h-11 rounded-xl px-4 font-medium transition-all duration-200 hover:scale-100',
    active
      ? 'border-primary/60 bg-primary/10 text-foreground hover:bg-primary/15'
      : 'border-border/80 bg-background/60 text-muted-foreground hover:border-accent/60 hover:text-foreground'
  );

export function GPUFilterBar({
  filters,
  onFiltersChange,
  resultCount,
  activeClass,
  onClassChange,
  classCounts,
  sort,
  onSortChange,
  onClearAll,
}: GPUFilterBarProps) {
  const [priceOpen, setPriceOpen] = useState(false);
  // The range being dragged, until it is committed to the URL; otherwise the
  // slider shows the applied filter.
  const [dragRange, setDragRange] = useState<[number, number] | null>(null);
  const localPriceRange = dragRange ?? filters.priceRange;

  const priceActive =
    filters.priceRange[0] !== PRICE_RANGE.min || filters.priceRange[1] !== PRICE_RANGE.max;
  const hasActiveFilters =
    filters.gpuType !== null ||
    filters.region !== null ||
    priceActive ||
    activeClass !== 'all' ||
    sort !== 'featured';

  // Only rentable GPUs are offered as a filter: a link to a product nobody can
  // rent is a dead end (same rule as GPU_TYPES in lib/catalog/filters.ts).
  const rentable = getAvailableGPUs();
  const selectedGpu = GPU_PRODUCTS.find((g) => g.id === filters.gpuType);
  const selectedRegion = REGION_OPTIONS.find((r) => r.id === filters.region);
  const priceLabel = `${formatCurrency(filters.priceRange[0])} – ${formatCurrency(filters.priceRange[1])}`;
  const sortLabel = SORTS.find((s) => s.id === sort)?.label ?? 'Sort';

  return (
    <div className="ga-console rounded-2xl p-3 sm:p-4">
      {/* GPU classes */}
      <div
        role="group"
        aria-label="GPU class"
        className="scrollbar-none -mx-1 flex gap-2 overflow-x-auto px-1 pb-2 pt-0.5"
      >
        {ARCADE_CLASSES.map((cls) => {
          const Icon = CLASS_ICONS[cls.id];
          const active = activeClass === cls.id;
          return (
            <button
              key={cls.id}
              type="button"
              aria-pressed={active}
              data-active={active}
              onClick={() => onClassChange(cls.id)}
              style={{ '--hue': cls.hue } as CSSProperties}
              className="ga-tab flex shrink-0 items-center gap-2.5 rounded-xl px-4 py-2.5 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-5"
            >
              <span className="ga-tab-led h-2 w-2 rounded-[2px]" />
              <Icon className={cn('size-4', active && 'text-[var(--hue)]')} />
              {cls.label}
              <span className="rounded-md bg-background/60 px-1.5 py-0.5 font-mono text-[11px] tabular-nums text-muted-foreground">
                {classCounts[cls.id]}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border/50 pt-3">
        {/* GPU model */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className={triggerClass(!!filters.gpuType)}>
              <IconCpu className="size-4 opacity-70" />
              {selectedGpu?.name.replace(/^NVIDIA\s+/, '') || 'GPU model'}
              <IconChevronDown className="size-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64 rounded-xl p-1">
            <DropdownMenuLabel className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              GPU model
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1" />
            <FilterItem
              label="All GPUs"
              selected={!filters.gpuType}
              onSelect={() => onFiltersChange({ ...filters, gpuType: null })}
            />
            {rentable.map((gpu) => (
              <FilterItem
                key={gpu.id}
                label={gpu.name}
                selected={filters.gpuType === gpu.id}
                onSelect={() => onFiltersChange({ ...filters, gpuType: gpu.id })}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Region */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className={triggerClass(!!filters.region)}>
              <IconWorld className="size-4 opacity-70" />
              <span className="max-w-[180px] truncate">{selectedRegion?.name || 'Region'}</span>
              <IconChevronDown className="size-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-80 w-72 overflow-y-auto rounded-xl p-1">
            <DropdownMenuLabel className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Region
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1" />
            <FilterItem
              label="All regions"
              selected={!filters.region}
              onSelect={() => onFiltersChange({ ...filters, region: null })}
            />
            {REGION_GROUPS.map((group) => (
              <div key={group}>
                <DropdownMenuLabel className="px-2 pb-1 pt-3 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group}
                </DropdownMenuLabel>
                {REGION_OPTIONS.filter((r) => r.group === group).map((region) => (
                  <FilterItem
                    key={region.id}
                    label={region.name}
                    selected={filters.region === region.id}
                    onSelect={() => onFiltersChange({ ...filters, region: region.id })}
                  />
                ))}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Price */}
        <DropdownMenu open={priceOpen} onOpenChange={setPriceOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className={triggerClass(priceActive)}>
              <IconCurrencyEuro className="size-4 opacity-70" />
              {priceActive ? priceLabel : 'Price'}
              <IconChevronDown className="size-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80 rounded-xl p-5">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Base rate per hour
            </p>
            <p className="mb-4 text-xs text-muted-foreground">
              Before the region and upload bandwidth adjustments.
            </p>
            <div className="space-y-5">
              <Slider
                value={localPriceRange}
                onValueChange={(value) => setDragRange(value as [number, number])}
                onValueCommit={(value) => {
                  setDragRange(null);
                  onFiltersChange({ ...filters, priceRange: value as [number, number] });
                }}
                min={PRICE_RANGE.min}
                max={PRICE_RANGE.max}
                step={PRICE_RANGE.step}
                className="w-full"
              />
              <div className="flex items-center justify-between">
                <div className="rounded-lg bg-muted px-3 py-2">
                  <span className="text-xs text-muted-foreground">Min</span>
                  <p className="text-sm font-semibold tabular-nums text-foreground">
                    {formatHourlyRate(localPriceRange[0])}
                  </p>
                </div>
                <div className="h-px w-8 bg-border" />
                <div className="rounded-lg bg-muted px-3 py-2">
                  <span className="text-xs text-muted-foreground">Max</span>
                  <p className="text-sm font-semibold tabular-nums text-foreground">
                    {formatHourlyRate(localPriceRange[1])}
                  </p>
                </div>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Sort */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className={triggerClass(sort !== 'featured')}>
              <IconArrowsSort className="size-4 opacity-70" />
              {sortLabel}
              <IconChevronDown className="size-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 rounded-xl p-1">
            <DropdownMenuLabel className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Sort by
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="my-1" />
            {SORTS.map((option) => (
              <FilterItem
                key={option.id}
                label={option.label}
                selected={sort === option.id}
                onSelect={() => onSortChange(option.id)}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="hidden flex-1 sm:block" />

        <div className="flex w-full items-center justify-between gap-3 sm:w-auto">
          <p aria-live="polite" className="text-sm text-muted-foreground">
            <span className="font-heading text-xl font-bold tabular-nums text-foreground">{resultCount}</span>{' '}
            {resultCount === 1 ? 'GPU' : 'GPUs'} ready to rent
          </p>
          <Button
            type="button"
            variant="ghost"
            onClick={onClearAll}
            disabled={!hasActiveFilters}
            className="h-10 rounded-xl px-3 text-sm font-semibold text-foreground hover:bg-accent/15"
          >
            <IconX className="size-4" />
            Clear filters
          </Button>
        </div>
      </div>
    </div>
  );
}

function FilterItem({
  label,
  selected,
  onSelect,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      onClick={onSelect}
      className={cn('mx-1 cursor-pointer rounded-lg text-sm', selected && 'bg-primary/10 text-foreground')}
    >
      <div className="flex w-full items-center justify-between">
        <span>{label}</span>
        {selected && <IconCheck className="size-4 text-primary" />}
      </div>
    </DropdownMenuItem>
  );
}
